import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FLOW_TYPES, isPalletEligibleForFlow } from '../utils/gateoutApi';
import CreateGateOutPage from './CreateGateOutPage';
import { Snackbar, Alert as MuiAlert } from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS, STATUS_OPTIONS, SHIFT_OPTIONS } from '../config';
import EditEntryModal from '../components/modals/EditEntryModal';
import PalletEditModal from '../components/modals/PalletEditModal';
import AddLineEntryModal from '../components/modals/AddLineEntryModal';
import './ColdStoreInPage.css';

// ============================================================
// Phase 2A.2 — Cold Store IN page (rewritten v5, May 5 2026)
// ------------------------------------------------------------
// Visual design aligned with Bapatla Factory Attendance theme:
//   · red→purple→blue gradient on top nav / primary buttons
//     (#C0392B → #8E44AD → #1F618D)
//   · solid purple table header (#8E44AD) with white text
//   · cream row hover (#FEF5E7)
//   · Arial / Helvetica font
//
// ⭐ Grouping rule: ALL 13 fields are group keys. ANY of them
// differing creates a new line.
// ============================================================

// ---- Constants -----------------------------------------------------------

// 13 grouping fields (all yellow). ANY change = new line.
const GROUP_KEYS = [
  'production_date', 'status', 'shift', 'product', 'grade',
  'pack_style', 'pack_size', 'brand', 'nav_id', 'product_id',
  'lot_no', 'day_code', 'trace_id',
];

const PARENT_COLUMNS = [
  { key: 'production_date', label: 'PRODUCTION DATE', isGroupKey: true,  filter: 'text' },
  { key: 'status',          label: 'STATUS',          isGroupKey: true,  filter: 'select', options: STATUS_OPTIONS },
  { key: 'shift',           label: 'SHIFT',           isGroupKey: true,  filter: 'select', options: SHIFT_OPTIONS },
  { key: 'product',         label: 'PRODUCT',         isGroupKey: true,  filter: 'text' },
  { key: 'grade',           label: 'GRADE',           isGroupKey: true,  filter: 'text' },
  { key: 'pack_style',      label: 'PACK STYLE',      isGroupKey: true,  filter: 'text' },
  { key: 'pack_size',       label: 'PACK SIZE',       isGroupKey: true,  filter: 'text' },
  { key: 'brand',           label: 'BRAND',           isGroupKey: true,  filter: 'text' },
  { key: 'nav_id',          label: 'NAV ID',          isGroupKey: true,  filter: 'text' },
  { key: 'product_id',      label: 'PRODUCT ID',      isGroupKey: true,  filter: 'text' },
  { key: 'lot_no',          label: 'LOT NO',          isGroupKey: true,  filter: 'text' },
  { key: 'day_code',        label: 'DAY CODE',        isGroupKey: true,  filter: 'text' },
  { key: 'trace_id',        label: 'TRACE ID',        isGroupKey: true,  filter: 'text' },
  { key: '_pallets',        label: 'PALLETS',         isGroupKey: false, filter: 'min',  numeric: true, computed: true },
  { key: '_cartons',        label: 'CARTONS',         isGroupKey: false, filter: 'min',  numeric: true, computed: true },
  { key: '_wt_kg',          label: 'WT (KG)',         isGroupKey: false, filter: 'min',  numeric: true, computed: true },
  { key: '_wt_lb',          label: 'WT (LB)',         isGroupKey: false, filter: 'min',  numeric: true, computed: true },
];

const PALLET_COLUMNS = [
  { key: 'pallet_id',    label: 'Pallet ID' },
  { key: 'cartons',      label: 'No of Cartons',    numeric: true },
  { key: 'loose_bags',   label: 'No of Loose Bags', numeric: true },
  { key: 'loose_kgs',    label: 'Loose KGs',        numeric: true, decimals: 2 },
  { key: 'cs1',          label: 'CS1 (Bin)' },
  { key: 'cs2',          label: 'CS2 (Bin)' },
  { key: 'total_kgs',    label: 'Total KGs',        numeric: true, decimals: 2 },
  { key: 'total_pounds', label: 'Total Pounds',     numeric: true, decimals: 2 },
];

const STATUS_PILL_CLASS = {
  'PRODUCED':         'produced',
  'REPACK-OUT':       'repack-out',
  'REPRINT-OUT':      'reprint-out',
  'REPROCESS-OUT':    'reprocess-out',
  'SHIPPED':          'shipped',
  'EXCESS-RETURNED':  'excess',
  'FCL EXCESS':       'excess',
  'QC SAMPLE':        'qc-sample',
  'ONLINE MIX':       'online-mix',
};

const POLL_INTERVAL_MS = 30000;

// ---- Helpers ------------------------------------------------------------

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

const normalizeDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
  return d;
};

const lineKey = (row) => GROUP_KEYS.map(k => String(row[k] ?? '')).join('|');

const fmtNum = (v, decimals) => {
  if (v == null || v === '') return '';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  return decimals != null ? n.toFixed(decimals) : Math.round(n);
};

const lineTotals = (line) => ({
  pallets: line.pallets.length,
  cartons: line.pallets.reduce((s, p) => s + (parseInt(p.cartons) || 0), 0),
  wt_kg: line.pallets.reduce((s, p) => s + (parseFloat(p.total_kgs) || 0), 0),
  wt_lb: line.pallets.reduce((s, p) => s + (parseFloat(p.total_pounds) || 0), 0),
});

// ============================================================
// Component
// ============================================================
export default function ColdStoreInPage() {
  // Gate-Out overlay state — when set, swaps the page content for CreateGateOutPage.
  // null = show inventory list; { flow_type, preloadedLines } = show gate-out form.
  const [gateOutMode, setGateOutMode] = useState(null);
  // -------- state --------
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [editTarget, setEditTarget] = useState(null);        // line edit (full)
  const [palletEditTarget, setPalletEditTarget] = useState(null); // pallet edit (limited)
  const [showAddModal, setShowAddModal] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [openKebab, setOpenKebab] = useState(null); // { kind: 'line'|'pallet', id }
  // Pagination
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const pollRef = useRef(null);

  // -------- data fetch --------
  const fetchInventory = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const params = new URLSearchParams({ view: 'daily_inventory' });
      const res = await axios.get(`${API_ENDPOINTS.INVENTORY}?${params}`, getAuthConfig());
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setRawRows(rows.map(r => ({ ...r, production_date: normalizeDate(r.production_date) })));
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
    pollRef.current = setInterval(() => fetchInventory(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchInventory]);

  // Close kebab on outside click
  useEffect(() => {
    if (!openKebab) return;
    const onDocClick = () => setOpenKebab(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openKebab]);

  // -------- group by all 13 keys --------
  const groupedLines = useMemo(() => {
    const map = new Map();
    rawRows.forEach(row => {
      const key = lineKey(row);
      if (!map.has(key)) {
        const lineMeta = {};
        GROUP_KEYS.forEach(k => { lineMeta[k] = row[k] ?? ''; });
        map.set(key, { id: key, ...lineMeta, pallets: [] });
      }
      map.get(key).pallets.push(row);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.production_date !== b.production_date) return (b.production_date || '').localeCompare(a.production_date || '');
      if (a.status !== b.status) return (a.status || '').localeCompare(b.status || '');
      return (a.product || '').localeCompare(b.product || '');
    });
  }, [rawRows]);

  // -------- filter --------
  const visibleLines = useMemo(() => {
    const f = filters;
    const matchText = (val, q) => String(val || '').toLowerCase().includes(String(q).toLowerCase());
    return groupedLines.filter(line => {
      for (const col of PARENT_COLUMNS) {
        const v = f[col.key];
        if (!v) continue;
        if (col.isGroupKey) {
          if (col.filter === 'select') {
            if (line[col.key] !== v) return false;
          } else if (!matchText(line[col.key], v)) return false;
        } else if (col.computed && col.numeric) {
          const t = lineTotals(line);
          const tgt =
            col.key === '_pallets' ? t.pallets :
            col.key === '_cartons' ? t.cartons :
            col.key === '_wt_kg'   ? t.wt_kg :
            col.key === '_wt_lb'   ? t.wt_lb : 0;
          if (tgt < parseFloat(v)) return false;
        }
      }
      return true;
    });
  }, [groupedLines, filters]);

  // -------- KPIs --------
  const kpis = useMemo(() => {
    const totalInCs = rawRows.filter(r => r.status === 'PRODUCED').length;
    const lots = new Set(rawRows.filter(r => r.status === 'PRODUCED').map(r => r.lot_no || '_')).size;
    const out = rawRows.filter(r => /-OUT$/.test(r.status || '')).length;
    const shipped = rawRows.filter(r => r.status === 'SHIPPED');
    const shipped24 = shipped.filter(r => {
      if (!r.shipped_at && !r.updated_at) return false;
      const dt = new Date(r.shipped_at || r.updated_at);
      return Date.now() - dt.getTime() <= 24 * 3600 * 1000;
    });
    const shipKg = shipped24.reduce((s, r) => s + (parseFloat(r.total_kgs) || 0), 0);
    const shipLb = shipped24.reduce((s, r) => s + (parseFloat(r.total_pounds) || 0), 0);
    const excess = rawRows.filter(r => r.status === 'EXCESS-RETURNED' || r.status === 'FCL EXCESS').length;
    return {
      totalInCs, lots, out, shipped24: shipped24.length, shipKg, shipLb, excess,
      totalEntries: rawRows.length, totalPallets: rawRows.length,
    };
  }, [rawRows]);

  // -------- pagination --------
  const totalPages = Math.max(1, Math.ceil(visibleLines.length / pageSize));
  const pagedLines = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return visibleLines.slice(start, start + pageSize);
  }, [visibleLines, currentPage, pageSize]);
  const pagedPalletCount = useMemo(
    () => pagedLines.reduce((s, l) => s + l.pallets.length, 0),
    [pagedLines]
  );
  // Reset to page 1 when filters or page size change
  useEffect(() => { setCurrentPage(1); }, [filters, pageSize]);
  // Clamp current page if data shrinks
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // -------- selection stats --------
  const selectionStats = useMemo(() => {
    let cartons = 0, kg = 0, lb = 0;
    selected.forEach(id => {
      const row = rawRows.find(r => String(r.id) === String(id));
      if (!row) return;
      cartons += parseInt(row.cartons) || 0;
      kg += parseFloat(row.total_kgs) || 0;
      lb += parseFloat(row.total_pounds) || 0;
    });
    return { count: selected.size, cartons, kg, lb };
  }, [selected, rawRows]);

  // -------- handlers --------
  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({});
  const clearSelection = () => setSelected(new Set());

  const togglePallet = (id, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(String(id)); else next.delete(String(id));
      return next;
    });
  };
  const toggleLine = (line, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      line.pallets.forEach(p => {
        if (checked) next.add(String(p.id)); else next.delete(String(p.id));
      });
      return next;
    });
  };
  const toggleAll = (checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      pagedLines.forEach(l => l.pallets.forEach(p => {
        if (checked) next.add(String(p.id)); else next.delete(String(p.id));
      }));
      return next;
    });
  };
  const toggleExpand = (lineId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  const isLineFullySelected = (line) => line.pallets.every(p => selected.has(String(p.id)));
  const isLinePartiallySelected = (line) =>
    !isLineFullySelected(line) && line.pallets.some(p => selected.has(String(p.id)));

  const handleEditLine = (line) => {
    setOpenKebab(null);
    setEditTarget(line.pallets[0]); // full edit on the first pallet of the line
  };
  const handleEditPallet = (pallet) => {
    setOpenKebab(null);
    setPalletEditTarget(pallet);
  };
  const handleEditSuccess = () => {
    setSnackbar({ severity: 'success', msg: 'Inventory updated successfully' });
    setEditTarget(null);
    setPalletEditTarget(null);
    fetchInventory(true);
    setSelected(new Set());
  };
  const handleSelectionAction = (flowType) => {
    setOpenKebab(null);
    const selectedIds = Array.from(selected);
    if (selectedIds.length === 0) {
      setSnackbar({ severity: 'info', msg: 'Select pallets first.' });
      return;
    }

    // Pull full row objects for selected pallets
    const selectedRows = rawRows.filter(r => selected.has(String(r.id)));

    // Filter to those eligible for this flow type
    const eligibleRows = selectedRows.filter(r => isPalletEligibleForFlow(r.status, flowType));
    if (eligibleRows.length === 0) {
      setSnackbar({
        severity: 'warning',
        msg: `None of the selected pallets are eligible for ${FLOW_TYPES[flowType].label}. Check their current statuses.`,
      });
      return;
    }
    if (eligibleRows.length < selectedRows.length) {
      const skipped = selectedRows.length - eligibleRows.length;
      setSnackbar({
        severity: 'info',
        msg: `${skipped} ineligible pallet${skipped === 1 ? '' : 's'} excluded from ${FLOW_TYPES[flowType].label}.`,
      });
    }

    // Group eligible rows by line key for the create page
    const linesMap = new Map();
    eligibleRows.forEach(r => {
      const key = lineKey(r);
      if (!linesMap.has(key)) {
        linesMap.set(key, {
          line_id: key,
          product: r.product, grade: r.grade, pack_size: r.pack_size,
          brand: r.brand, lot_no: r.lot_no, day_code: r.day_code,
          status: r.status,
          pallets: [],
        });
      }
      linesMap.get(key).pallets.push(r);
    });

    setGateOutMode({
      flow_type: flowType,
      preloadedLines: Array.from(linesMap.values()),
    });
  };
  const handleAddEntry = () => {
    setShowAddModal(true);
  };
  const handleAddSuccess = () => {
    setShowAddModal(false);
    setSnackbar({ severity: 'success', msg: 'New entries added — refreshing list' });
    fetchInventory(true);
  };

  const handleExportExcel = () => {
    const headers = [
      ...PARENT_COLUMNS.filter(c => !c.computed).map(c => c.label),
      'PALLETS', 'CARTONS', 'WT (KG)', 'WT (LB)',
    ];
    const rows = visibleLines.map(line => {
      const t = lineTotals(line);
      return [
        line.production_date, line.status, line.shift, line.product, line.grade,
        line.pack_style, line.pack_size, line.brand, line.nav_id, line.product_id,
        line.lot_no, line.day_code, line.trace_id,
        t.pallets, t.cartons, t.wt_kg.toFixed(2), Math.round(t.wt_lb),
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cold-store-in-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // -------- render helpers --------
  const renderStatusPill = (status) => {
    const cls = STATUS_PILL_CLASS[status] || 'produced';
    return <span className={`status-pill ${cls}`}>{status || '—'}</span>;
  };

  const renderColumnFilter = (col) => {
    const value = filters[col.key] || '';
    if (col.filter === 'select') {
      return (
        <select value={value} onChange={(e) => setFilter(col.key, e.target.value)}>
          <option value="">All</option>
          {(col.options || []).filter(o => o).map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => setFilter(col.key, e.target.value)}
        placeholder={col.filter === 'min' ? '≥' : 'Filter…'}
      />
    );
  };

  const allCheckedOnPage =
    pagedLines.length > 0 &&
    pagedLines.every(l => l.pallets.every(p => selected.has(String(p.id))));

  const anyFiltersActive = Object.values(filters).some(v => v);

  // ============================================================
  // RENDER
  // ============================================================

  // OVERLAY MODE — when user clicks an action button, swap the inventory
  // view for the gate-out form. Same route, different content.
  if (gateOutMode) {
    return (
      <CreateGateOutPage
        flowType={gateOutMode.flow_type}
        preloadedLines={gateOutMode.preloadedLines}
        onSuccess={() => {
          setGateOutMode(null);
          setSelected(new Set());
          fetchInventory(true);   // refresh CS IN — gate-outed pallets gone
          setSnackbar({ severity: 'success', msg: 'Gate-out completed. Inventory refreshed.' });
        }}
        onCancel={() => setGateOutMode(null)}
      />
    );
  }

  return (
    <div className="cs-in-page">

      {/* PAGE HEADER */}
      <div className="page-header">
        <h1>📦 Cold Store IN — Daily Inventory</h1>
        <span className="live-indicator">
          <span className="live-dot" />
          {refreshing
            ? 'Refreshing…'
            : `Live · ${kpis.totalEntries.toLocaleString()} entries · ${kpis.totalPallets.toLocaleString()} pallets · Auto-refresh ON`}
        </span>
        <div className="actions">
          <button className="btn" onClick={() => fetchInventory(true)}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={handleAddEntry}>+ Add Entry</button>
        </div>
      </div>

      {/* ERROR */}
      {error && <div className="alert-error">⚠️ {error}</div>}

      {/* KPI CARDS */}
      <div className="kpi-grid">
        <div className="kpi">
          <label>Total Pallets in CS</label>
          <div className="val">{kpis.totalInCs.toLocaleString()}</div>
          <div className="sub">Across {kpis.lots.toLocaleString()} lots</div>
        </div>
        <div className="kpi kpi-out">
          <label>Pallets Out</label>
          <div className="val">{kpis.out.toLocaleString()}</div>
          <div className="sub">Repack / Reprint / Reprocess</div>
        </div>
        <div className="kpi kpi-ship">
          <label>Pallets Shipped (24H)</label>
          <div className="val">{kpis.shipped24.toLocaleString()}</div>
          <div className="sub">{kpis.shipKg.toFixed(0)} kg / {Math.round(kpis.shipLb).toLocaleString()} lb</div>
        </div>
        <div className="kpi kpi-excess">
          <label>Excess Returns</label>
          <div className="val">{kpis.excess.toLocaleString()}</div>
          <div className="sub">Pending review</div>
        </div>
      </div>

      {/* SELECTION BAR */}
      {selectionStats.count > 0 && (
        <div className="selection-bar">
          <span className="selection-summary">
            <span className="check">✓</span>
            <span className="count">{selectionStats.count} pallet{selectionStats.count === 1 ? '' : 's'}</span> selected ·{' '}
            <span>{selectionStats.cartons} cartons</span> ·{' '}
            <span>{selectionStats.kg.toFixed(1)} kg / {Math.round(selectionStats.lb).toLocaleString()} lb</span>
          </span>
          <div className="actions">
            <button className="action-btn repack"    onClick={() => handleSelectionAction('REPACK')}>↗ Repack-Out</button>
            <button className="action-btn reprint"   onClick={() => handleSelectionAction('REPRINT')}>↗ Reprint-Out</button>
            <button className="action-btn reprocess" onClick={() => handleSelectionAction('REPROCESS')}>↗ Reprocess-Out</button>
            <button className="action-btn qc"        onClick={() => handleSelectionAction('QC')}>🔬 QC Sample</button>
            <button className="action-btn ship"      onClick={() => handleSelectionAction('SHIPMENT')}>↗ Make Shipment</button>
            <button className="action-btn clear"     onClick={clearSelection}>Clear</button>
          </div>
        </div>
      )}

      {/* TOP FILTERS BAR */}
      <div className="filters-bar">
        <div className="filter-field">
          <label>Status:</label>
          <select value={filters.status || ''} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">All</option>
            {(STATUS_OPTIONS || []).filter(o => o).map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <label>Day Code:</label>
          <input type="text" value={filters.day_code || ''}
                 onChange={(e) => setFilter('day_code', e.target.value)} placeholder="6D26" />
        </div>
        <div className="filter-field">
          <label>Lot:</label>
          <input type="text" value={filters.lot_no || ''}
                 onChange={(e) => setFilter('lot_no', e.target.value)} placeholder="L-2604..." />
        </div>
        <div className="filter-field search">
          <input type="text" value={filters.trace_id || ''}
                 onChange={(e) => setFilter('trace_id', e.target.value)}
                 placeholder="🔍 Search Trace ID…" />
        </div>
        <div className="filter-actions">
          {anyFiltersActive && (
            <button className="btn btn-clear-filters" onClick={clearFilters}>Clear Filters</button>
          )}
          <button className="btn-export" onClick={handleExportExcel}>📥 Export Excel</button>
        </div>
      </div>

      {/* GROUP KEY LEGEND */}
      <div className="group-key-legend">
        <span>
          Lines are grouped by 13 fields (production date, status, shift, product,
          grade, pack style/size, brand, NAV ID, product ID, lot no, day code, trace ID).
          Any difference in any field creates a new line.
        </span>
      </div>

      {/* TABLE */}
      <div className="table-wrap">
        {loading && rawRows.length === 0 ? (
          <div className="table-empty">
            <span className="icon">⏳</span>
            Loading inventory…
          </div>
        ) : visibleLines.length === 0 ? (
          <div className="table-empty">
            <span className="icon">📦</span>
            <div>{anyFiltersActive ? 'No matches for current filters.' : 'No inventory in cold store.'}</div>
            {anyFiltersActive && (
              <div><button className="btn" onClick={clearFilters}>Clear filters</button></div>
            )}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="grouped">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: 36 }}>
                    <input type="checkbox"
                           checked={allCheckedOnPage}
                           onChange={(e) => toggleAll(e.target.checked)} />
                  </th>
                  {PARENT_COLUMNS.map(col => (
                    <th key={col.key}
                        className={col.numeric ? 'num' : ''}>
                      {col.label}
                    </th>
                  ))}
                  <th rowSpan={2} style={{ width: 56 }}>ACTIONS</th>
                </tr>
                <tr className="filter-row">
                  {PARENT_COLUMNS.map(col => (
                    <th key={col.key}>
                      {renderColumnFilter(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedLines.map(line => {
                  const isExp = expanded.has(line.id);
                  const fullSel = isLineFullySelected(line);
                  const partSel = isLinePartiallySelected(line);
                  const t = lineTotals(line);
                  const lineKebabOpen = openKebab && openKebab.kind === 'line' && openKebab.id === line.id;
                  return (
                    <React.Fragment key={line.id}>
                      <tr className={`line${isExp ? ' expanded' : ''}`}
                          onClick={() => toggleExpand(line.id)}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={fullSel}
                            ref={(el) => { if (el) el.indeterminate = partSel; }}
                            onChange={(e) => toggleLine(line, e.target.checked)}
                          />
                        </td>
                        <td>{line.production_date || '—'}</td>
                        <td>{renderStatusPill(line.status)}</td>
                        <td>{line.shift || '—'}</td>
                        <td>{line.product || '—'}</td>
                        <td>{line.grade || '—'}</td>
                        <td>{line.pack_style || '—'}</td>
                        <td>{line.pack_size || '—'}</td>
                        <td>{line.brand || '—'}</td>
                        <td>{line.nav_id || '—'}</td>
                        <td>{line.product_id || '—'}</td>
                        <td>
                          <span className="lot-cell">
                            <span className="toggle">▾</span>
                            <strong>{line.lot_no || '—'}</strong>
                            <span className="pallets-count">{t.pallets}</span>
                          </span>
                        </td>
                        <td>{line.day_code || '—'}</td>
                        <td>{line.trace_id || '—'}</td>
                        <td className="num"><strong>{t.pallets}</strong></td>
                        <td className="num"><strong>{t.cartons.toLocaleString()}</strong></td>
                        <td className="num"><strong>{t.wt_kg.toFixed(2)}</strong></td>
                        <td className="num"><strong>{Math.round(t.wt_lb).toLocaleString()}</strong></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="kebab-wrap">
                            <button
                              className="kebab"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenKebab(lineKebabOpen ? null : { kind: 'line', id: line.id });
                              }}>
                              ⋮
                            </button>
                            {lineKebabOpen && (
                              <div className="kebab-menu" onClick={(e) => e.stopPropagation()}>
                                <button onClick={() => handleEditLine(line)}>✏️ Edit Line</button>
                                <div className="sep" />
                                <button onClick={() => handleSelectionAction('Repack-Out')}>↗ Repack-Out</button>
                                <button onClick={() => handleSelectionAction('Reprint-Out')}>↗ Reprint-Out</button>
                                <button onClick={() => handleSelectionAction('Reprocess-Out')}>↗ Reprocess-Out</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExp && (
                        <tr className="pallet-block">
                          <td colSpan={PARENT_COLUMNS.length + 2}>
                            <div className="pallet-subtable-wrap">
                              <table className="subtable">
                                <thead>
                                  <tr>
                                    <th style={{ width: 30 }}></th>
                                    {PALLET_COLUMNS.map(col => (
                                      <th key={col.key} className={col.numeric ? 'num' : ''}>{col.label}</th>
                                    ))}
                                    <th style={{ width: 56 }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {line.pallets.map(p => {
                                    const sel = selected.has(String(p.id));
                                    const palletKebabOpen = openKebab && openKebab.kind === 'pallet' && openKebab.id === p.id;
                                    return (
                                      <tr key={p.id}>
                                        <td>
                                          <input type="checkbox" checked={sel}
                                                 onChange={(e) => togglePallet(p.id, e.target.checked)} />
                                        </td>
                                        {PALLET_COLUMNS.map(col => {
                                          const v = p[col.key];
                                          const display = col.numeric ? fmtNum(v, col.decimals) : (v ?? '—');
                                          if (col.key === 'pallet_id') {
                                            return <td key={col.key}><span className="pallet-id-text">{display}</span></td>;
                                          }
                                          return <td key={col.key} className={col.numeric ? 'num' : ''}>{display}</td>;
                                        })}
                                        <td>
                                          <div className="kebab-wrap">
                                            <button
                                              className="kebab"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenKebab(palletKebabOpen ? null : { kind: 'pallet', id: p.id });
                                              }}>
                                              ⋮
                                            </button>
                                            {palletKebabOpen && (
                                              <div className="kebab-menu" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => handleEditPallet(p)}>✏️ Edit Pallet</button>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {visibleLines.length > 0 && (
          <div className="pager-footer">
            <div className="pager-summary">
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong>–
              <strong>{Math.min(currentPage * pageSize, visibleLines.length)}</strong>{' '}
              of <strong>{visibleLines.length.toLocaleString()}</strong> lines ·{' '}
              <strong>{pagedPalletCount.toLocaleString()}</strong> pallets on this page
            </div>
            <div className="pager-controls">
              <label>Per page:</label>
              <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
              <div className="pager-buttons">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} title="First">«</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} title="Previous">‹</button>
                {(() => {
                  // Show up to 5 page numbers around current
                  const pages = [];
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(totalPages, start + 4);
                  for (let p = start; p <= end; p++) pages.push(p);
                  return pages.map(p => (
                    <button key={p} onClick={() => setCurrentPage(p)}
                            className={p === currentPage ? 'active' : ''}>
                      {p}
                    </button>
                  ));
                })()}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} title="Next">›</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} title="Last">»</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit modals */}
      {editTarget && (
        <EditEntryModal
          open={!!editTarget}
          entryData={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={handleEditSuccess}
        />
      )}
      {palletEditTarget && (
        <PalletEditModal
          open={!!palletEditTarget}
          entryData={palletEditTarget}
          onClose={() => setPalletEditTarget(null)}
          onSuccess={handleEditSuccess}
        />
      )}
      <AddLineEntryModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar ? (
          <MuiAlert severity={snackbar.severity || 'info'} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.msg}
          </MuiAlert>
        ) : null}
      </Snackbar>
    </div>
  );
}
